/**
 * "Open in Google Sheets" helper — desktop + mobile paths.
 *
 * Google Sheets has no direct "import from authenticated URL" flow,
 * and our admin CSVs are gated behind a bearer token — so we can't
 * just open a `?import=` link. We pick the right strategy per device:
 *
 * DESKTOP (popup-blocker friendly, clipboard-first):
 *   1. Open https://docs.google.com/spreadsheets/u/0/create SYNCHRONOUSLY
 *      inside the user-gesture stack so popup blockers allow it.
 *   2. Fetch CSV → copy to clipboard so ⌘V/Ctrl-V in cell A1 pastes the
 *      table directly.
 *   3. Auto-download the .csv as a hard fallback.
 *   4. Show a persistent instructions dialog.
 *
 * MOBILE (Sheets app handoff):
 *   - The Sheets iOS/Android app intercepts the desktop "/create" URL
 *     and treats it as an existing-file open intent → "File not found"
 *     error. Reported by users on iPhone Safari + the Sheets app.
 *   - Mobile clipboard writes also frequently fail silently in
 *     mobile Safari + WebViews.
 *   - So on mobile we DON'T open a Sheets tab. We just download the
 *     CSV file (mobile Safari triggers the "Save to Files" sheet) and
 *     show clear "open Sheets app → + → Import → pick file" guidance.
 *     This is the only flow that actually works on mobile.
 */
import { toast } from "sonner";

let _instructionsHandler = null;

/**
 * Register the React-side dialog renderer. Called once from
 * GoogleSheetsInstructionsDialog on mount so we can decouple the
 * imperative caller (anywhere in the codebase) from the JSX-side
 * component.
 */
export function registerSheetsInstructionsHandler(fn) {
  _instructionsHandler = fn;
}

function _showInstructions({ copied, filename, mobile }) {
  if (_instructionsHandler) {
    _instructionsHandler({ copied, filename, mobile, open: true });
  } else {
    // Fallback if the dialog isn't mounted yet — at least toast.
    if (mobile) {
      toast.message(
        `${filename} downloaded. Open the Google Sheets app, tap +, choose Import, and pick the file.`,
        { duration: 10000 },
      );
    } else if (copied) {
      toast.success(
        `CSV copied. In the new Sheet tab, click cell A1 and press ⌘V or Ctrl-V to paste.`,
        { duration: 9000 },
      );
    } else {
      toast.message(
        `Sheet opened. In the new tab, choose File → Import → Upload and pick ${filename}.`,
        { duration: 10000 },
      );
    }
  }
}

function _isMobile() {
  if (typeof navigator === "undefined") return false;
  // Touch-capable + narrow viewport OR iOS/Android UA.
  const ua = navigator.userAgent || "";
  const mobileUa = /iPhone|iPad|iPod|Android|Mobile/i.test(ua);
  const narrowViewport = typeof window !== "undefined" && window.innerWidth <= 820;
  return mobileUa || narrowViewport;
}

export async function openInGoogleSheets({
  api,
  endpoint,
  filenameHint = "report",
} = {}) {
  if (!api || !endpoint) {
    throw new Error("openInGoogleSheets requires `api` and `endpoint`");
  }

  const mobile = _isMobile();

  // Step 1 (desktop only) — open Sheets FIRST while we still have the
  // user gesture. Mobile skips this entirely because the Sheets app
  // intercepts /create and 404s.
  let popupBlocked = false;
  if (!mobile) {
    const sheetsWin = window.open(
      "https://docs.google.com/spreadsheets/u/0/create",
      "_blank",
      "noopener,noreferrer",
    );
    popupBlocked = !sheetsWin || sheetsWin.closed || typeof sheetsWin.closed === "undefined";
    if (popupBlocked) {
      toast.error("Google Sheets popup was blocked. We'll download the CSV instead.");
      // Fall through anyway — we still download the CSV so the user
      // gets value out of the click.
    }
  }

  // Step 2 — fetch the CSV.
  let text = "";
  let blob = null;
  try {
    const res = await api.get(endpoint, { responseType: "blob" });
    blob = res.data;
    text = await blob.text();
  } catch (err) {
    toast.error(err?.message || "Couldn't fetch the CSV. Try again or download manually.");
    throw err;
  }

  // Step 3 — clipboard copy (desktop only; mobile clipboard writes are
  // unreliable inside WebViews and don't help when there's no Sheets
  // tab to paste into).
  let copied = false;
  if (!mobile) {
    try {
      if (navigator.clipboard?.writeText && window.isSecureContext !== false) {
        await navigator.clipboard.writeText(text);
        copied = true;
      }
    } catch { /* permission denied or non-secure context */ }
    if (!copied) {
      // Legacy fallback for older browsers.
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-1000px";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        copied = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch { /* still nothing */ }
    }
  }

  // Step 4 — ALWAYS auto-download the CSV file as a hard fallback so
  // the user has a real artifact even if clipboard + popup both fail.
  // On mobile this is THE flow — it triggers the iOS "Save to Files"
  // / Android download sheet which the user then imports into Sheets.
  const filename = filenameHint.endsWith(".csv") ? filenameHint : `${filenameHint}.csv`;
  try {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 5000);
  } catch { /* download failed, oh well */ }

  // Step 5 — show the persistent instructions dialog.
  _showInstructions({ copied, filename, mobile });
}
